import React from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Wrench, Plus, Check, Phone, MessageSquare, Globe, Settings } from 'lucide-react';
import { BackButton } from '../components/common/BackButton';
import { CTAButton } from '../components/common/CTAButton';

interface BusinessModelsPageProps {
  hideHeading?: boolean;
}

export function BusinessModelsPage({ hideHeading = false }: BusinessModelsPageProps) {
  return (
    <div className={hideHeading ? '' : 'pt-16 min-h-screen'}>
      {!hideHeading && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <BackButton label="Back" />
        </div>
      )}
      <div className="py-12 bg-black/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!hideHeading && (
            <>
              <div className="text-center mb-8">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  Pricing & Packages
                </h1>
                <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-6">
                  Most clients start with the Missed Call & Lead Rescue System. You can see standard pricing here, or book a quick call if you're not sure what you need.
                </p>
              </div>

              <div className="max-w-3xl mx-auto text-center mb-12">
                <div className="inline-flex items-center gap-3 bg-blue-500/10 border border-blue-500/30 rounded-lg px-6 py-4">
                  <Phone className="w-6 h-6 text-blue-500 flex-shrink-0" />
                  <div className="text-left">
                    <p className="text-gray-300 text-sm mb-1">Not sure where to start?</p>
                    <Link
                      to="/#start-here"
                      className="text-blue-500 hover:text-blue-400 font-semibold inline-flex items-center gap-1"
                    >
                      Book a Free 15-Minute Audit →
                    </Link>
                  </div>
                </div>
              </div>
            </>
          )}

          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Lead Rescue System Pricing
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* One-Time Setup */}
            <div className="relative">
              <div className="relative p-8 rounded-lg border border-blue-500/20 backdrop-blur-sm h-full">
                <div className="flex items-center gap-4 mb-6">
                  <DollarSign className="w-8 h-8 text-blue-500" />
                  <h3 className="text-2xl font-bold text-white">One-Time Setup</h3>
                </div>
                <div className="mb-6">
                  <p className="text-3xl font-bold text-blue-500">$749</p>
                  <p className="text-gray-400 text-sm mt-1">One-time payment</p>
                </div>

                <p className="text-gray-300 mb-6">
                  Complete implementation of the Lead Rescue System tailored to your business.
                </p>

                <div className="space-y-4">
                  <ul className="space-y-3">
                    {[
                      'Setup and configuration',
                      'Message writing and approval',
                      'Lead log creation',
                      'Testing and go-live',
                      '24-hour implementation (Lead Rescue only)'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* System Care Plan */}
            <div className="relative">
              <div className="absolute -top-3 -right-3 z-10">
                <span className="px-4 py-1 bg-blue-500 text-white text-sm font-bold rounded-full">
                  Recommended
                </span>
              </div>
              <div className="relative p-8 rounded-lg border border-blue-500 backdrop-blur-sm h-full">
                <div className="flex items-center gap-4 mb-6">
                  <Wrench className="w-8 h-8 text-blue-500" />
                  <h3 className="text-2xl font-bold text-white">System Care Plan</h3>
                </div>
                <div className="mb-6">
                  <p className="text-3xl font-bold text-blue-500">$49/month</p>
                  <p className="text-gray-400 text-sm mt-1">Month-to-month, cancel anytime</p>
                </div>

                <p className="text-gray-300 mb-6">
                  Ongoing support and maintenance to keep everything running smoothly.
                </p>

                <div className="space-y-4">
                  <ul className="space-y-3">
                    {[
                      'Tool costs and hosting covered',
                      'Break/fix support',
                      'Updates when you change booking links',
                      'Lead log maintenance',
                      'Email support within 24 hours'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Optional Features Included */}
            <div className="relative">
              <div className="relative p-8 rounded-lg border border-blue-500/20 backdrop-blur-sm h-full">
                <div className="flex items-center gap-4 mb-6">
                  <Plus className="w-8 h-8 text-blue-500" />
                  <h3 className="text-2xl font-bold text-white">Optional Features</h3>
                </div>
                <p className="text-gray-300 mb-6">
                  These features are included as part of your system build at no additional cost.
                </p>

                <div className="space-y-4">
                  <ul className="space-y-3">
                    {[
                      'Gentle follow-up sequences',
                      'Review request automation',
                      'Reactivation campaigns',
                      'Optimization and improvements'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 pt-6 border-t border-blue-500/20">
                    <p className="text-gray-400 text-sm">
                      We'll discuss which features make sense for your business during setup and include them in the build.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-3xl mx-auto bg-black/40 border border-blue-500/20 rounded-lg p-8 mb-16">
            <h3 className="text-xl font-bold text-white mb-4">What's Included in Lead Rescue Setup</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm">Dedicated forwarding number</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm">Custom SMS messaging</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm">Website form integration</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm">Simple lead log setup</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm">Testing and training</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm">24-hour go-live</span>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-white mb-4 text-center">
              Additional Services
            </h2>
            <p className="text-center text-gray-400 mb-12 max-w-3xl mx-auto">
              These services require custom scoping based on your specific needs. Timeline and pricing vary by project complexity.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-black/30 border border-blue-500/20 rounded-lg p-8 flex flex-col">
                <MessageSquare className="w-12 h-12 text-blue-500 mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">Chatbots & Phone Agents</h3>
                <p className="text-gray-300 mb-6">
                  Handle FAQs, qualify leads, route calls, and reduce repetitive tasks for your team.
                </p>
                <div className="mb-6">
                  <p className="text-2xl font-bold text-blue-500">Custom Quote</p>
                  <p className="text-gray-400 text-sm mt-1">Based on scope and features</p>
                </div>
                <ul className="space-y-3 text-sm text-gray-300 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>FAQ handling and responses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Lead qualification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Call routing and management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Custom conversation flows</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500 mb-4">Timeline: 1-3 weeks typically</p>
                <div className="mt-auto">
                  <CTAButton href="/services/chatbots-phone-agents" variant="secondary">
                    Learn More
                  </CTAButton>
                </div>
              </div>

              <div className="bg-black/30 border border-blue-500/20 rounded-lg p-8 flex flex-col">
                <Globe className="w-12 h-12 text-blue-500 mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">Websites & Landing Pages</h3>
                <p className="text-gray-300 mb-6">
                  Clean, conversion-focused sites for local service businesses with fast loading and clear calls to action.
                </p>
                <div className="mb-6">
                  <p className="text-2xl font-bold text-blue-500">Custom Quote</p>
                  <p className="text-gray-400 text-sm mt-1">Based on pages and features</p>
                </div>
                <ul className="space-y-3 text-sm text-gray-300 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Mobile-responsive design</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Fast page loading</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Lead capture forms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Clear messaging and CTAs</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500 mb-4">Timeline: 2-4 weeks typically</p>
                <div className="mt-auto">
                  <CTAButton href="/services/websites-landing-pages" variant="secondary">
                    Learn More
                  </CTAButton>
                </div>
              </div>

              <div className="bg-black/30 border border-blue-500/20 rounded-lg p-8 flex flex-col">
                <Settings className="w-12 h-12 text-blue-500 mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">Custom Automations</h3>
                <p className="text-gray-300 mb-6">
                  Connect your existing tools so your business runs smoother with less manual work.
                </p>
                <div className="mb-6">
                  <p className="text-2xl font-bold text-blue-500">Custom Quote</p>
                  <p className="text-gray-400 text-sm mt-1">Based on complexity</p>
                </div>
                <ul className="space-y-3 text-sm text-gray-300 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Tool integration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Workflow automation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Data synchronization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Review campaigns</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500 mb-4">Timeline: 1-4 weeks typically</p>
                <div className="mt-auto">
                  <CTAButton href="/services/custom-automations" variant="secondary">
                    Learn More
                  </CTAButton>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <CTAButton href="/book-audit" variant="primary" icon>
              Book Your Free Lead Rescue Audit
            </CTAButton>
            <p className="text-gray-400 mt-4 text-sm">
              Let's talk about your setup and see if the system is right for you.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}